import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

/**
 * Turns a browser MediaRecorder blob into something a native iOS/Flutter player
 * can actually decode.
 *
 * The problem this solves:
 *   Chrome/Android MediaRecorder produces WebM/Opus. The gift site historically
 *   uploaded those bytes labelled `audio/wav`, and the iPhone Mia Memory app
 *   then failed playback with AVError -11800 (it cannot decode WebM). Safari is
 *   handled separately — it already records AAC in an MP4 container, so
 *   VoiceRecorder only relabels it.
 *
 * The fix:
 *   Decode the recording to PCM with the Web Audio API, then re-encode to AAC in
 *   an `.m4a` (MP4) container — the format the mobile app expects. Where the
 *   browser lacks the WebCodecs AAC encoder, fall back to real PCM WAV
 *   (RIFF/WAVE) bytes, which iOS also plays natively. Either way the bytes we
 *   upload genuinely match the declared content type.
 */

const TARGET_BITRATE = 128_000;
// Feed the encoder in ~100ms slices so we never build one enormous AudioData.
const CHUNK_SECONDS = 0.1;

export type UploadableContainer = 'mp4' | 'wav';

export type TranscodeResult = {
  blob: Blob;
  container: UploadableContainer;
};

// ─── Container sniffing (magic bytes) ─────────────────────────────────────────

/**
 * Identifies an audio container from its leading bytes, so we never trust — or
 * emit — a blob whose declared MIME type disagrees with its actual contents.
 *   WebM:    starts `1A 45 DF A3`
 *   MP4/M4A: `ftyp` (`66 74 79 70`) at offset 4
 *   WAV:     `RIFF` at 0 and `WAVE` at 8
 */
export function sniffAudioContainer(
  bytes: Uint8Array,
): 'webm' | 'mp4' | 'wav' | 'unknown' {
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x1a &&
    bytes[1] === 0x45 &&
    bytes[2] === 0xdf &&
    bytes[3] === 0xa3
  ) {
    return 'webm';
  }

  if (
    bytes.length >= 8 &&
    bytes[4] === 0x66 && // f
    bytes[5] === 0x74 && // t
    bytes[6] === 0x79 && // y
    bytes[7] === 0x70 // p
  ) {
    return 'mp4';
  }

  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 && // R
    bytes[1] === 0x49 && // I
    bytes[2] === 0x46 && // F
    bytes[3] === 0x46 && // F
    bytes[8] === 0x57 && // W
    bytes[9] === 0x41 && // A
    bytes[10] === 0x56 && // V
    bytes[11] === 0x45 // E
  ) {
    return 'wav';
  }

  return 'unknown';
}

// ─── WAV encoding (fallback) ──────────────────────────────────────────────────

/**
 * Encodes planar float PCM channels into a 16-bit RIFF/WAVE buffer. Pure and
 * side-effect free so it can be unit tested without any browser audio APIs.
 */
export function encodeWav(
  channels: Float32Array[],
  sampleRate: number,
): ArrayBuffer {
  const numChannels = Math.max(1, channels.length);
  const numFrames = channels[0]?.length ?? 0;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM fmt chunk length
  view.setUint16(20, 1, true); // audio format = PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let frame = 0; frame < numFrames; frame += 1) {
    for (let ch = 0; ch < numChannels; ch += 1) {
      const sample = Math.max(-1, Math.min(1, channels[ch]?.[frame] ?? 0));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return buffer;
}

function audioBufferToWavBlob(audioBuffer: AudioBuffer): Blob {
  const channels: Float32Array[] = [];
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch += 1) {
    channels.push(audioBuffer.getChannelData(ch));
  }
  return new Blob([encodeWav(channels, audioBuffer.sampleRate)], {
    type: 'audio/wav',
  });
}

// ─── AAC / MP4 encoding (preferred, via WebCodecs) ────────────────────────────
//
// WebCodecs (AudioEncoder / AudioData) is not present in every browser and is
// absent from the DOM lib typings here, so we reach for it off globalThis with
// narrow local shapes rather than assume the ambient globals exist.

type EncodedAudioChunkLike = unknown;
type EncodedAudioChunkMetadataLike = unknown;

interface AudioDataInitLike {
  format: string;
  sampleRate: number;
  numberOfFrames: number;
  numberOfChannels: number;
  timestamp: number;
  data: BufferSource;
}

interface AudioDataLike {
  close(): void;
}

interface AudioEncoderConfigLike {
  codec: string;
  sampleRate: number;
  numberOfChannels: number;
  bitrate?: number;
}

interface AudioEncoderInitLike {
  output: (
    chunk: EncodedAudioChunkLike,
    meta?: EncodedAudioChunkMetadataLike,
  ) => void;
  error: (error: DOMException) => void;
}

interface AudioEncoderLike {
  configure(config: AudioEncoderConfigLike): void;
  encode(data: AudioDataLike): void;
  flush(): Promise<void>;
  close(): void;
}

interface AudioEncoderCtor {
  new (init: AudioEncoderInitLike): AudioEncoderLike;
  isConfigSupported(
    config: AudioEncoderConfigLike,
  ): Promise<{ supported?: boolean }>;
}

interface AudioDataCtor {
  new (init: AudioDataInitLike): AudioDataLike;
}

interface MuxerLike {
  addAudioChunk(
    chunk: EncodedAudioChunkLike,
    meta?: EncodedAudioChunkMetadataLike,
  ): void;
  finalize(): void;
}

const webCodecs = () =>
  globalThis as unknown as {
    AudioEncoder?: AudioEncoderCtor;
    AudioData?: AudioDataCtor;
  };

async function isAacEncodingSupported(
  sampleRate: number,
  numberOfChannels: number,
): Promise<boolean> {
  const AudioEncoderRef = webCodecs().AudioEncoder;
  if (!AudioEncoderRef || typeof AudioEncoderRef.isConfigSupported !== 'function') {
    return false;
  }
  try {
    const result = await AudioEncoderRef.isConfigSupported({
      codec: 'mp4a.40.2', // AAC-LC
      sampleRate,
      numberOfChannels,
      bitrate: TARGET_BITRATE,
    });
    return Boolean(result?.supported);
  } catch {
    return false;
  }
}

async function encodeAacMp4(audioBuffer: AudioBuffer): Promise<Blob> {
  const { AudioEncoder: AudioEncoderRef, AudioData: AudioDataRef } = webCodecs();
  if (!AudioEncoderRef || !AudioDataRef) {
    throw new Error('WebCodecs AAC encoder is unavailable');
  }

  const { numberOfChannels, sampleRate, length } = audioBuffer;

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    fastStart: 'in-memory',
    audio: { codec: 'aac', numberOfChannels, sampleRate },
  });
  const muxerRef = muxer as unknown as MuxerLike;

  let encoderError: unknown = null;
  const encoder = new AudioEncoderRef({
    output: (chunk, meta) => muxerRef.addAudioChunk(chunk, meta),
    error: (error) => {
      encoderError = error;
    },
  });
  encoder.configure({
    codec: 'mp4a.40.2',
    sampleRate,
    numberOfChannels,
    bitrate: TARGET_BITRATE,
  });

  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numberOfChannels; ch += 1) {
    channels.push(audioBuffer.getChannelData(ch));
  }

  const chunkFrames = Math.max(1024, Math.floor(sampleRate * CHUNK_SECONDS));
  for (let offset = 0; offset < length; offset += chunkFrames) {
    const frames = Math.min(chunkFrames, length - offset);
    const planar = new Float32Array(frames * numberOfChannels);
    for (let ch = 0; ch < numberOfChannels; ch += 1) {
      planar.set(channels[ch].subarray(offset, offset + frames), ch * frames);
    }
    const audioData = new AudioDataRef({
      format: 'f32-planar',
      sampleRate,
      numberOfFrames: frames,
      numberOfChannels,
      timestamp: Math.round((offset / sampleRate) * 1_000_000),
      data: planar,
    });
    encoder.encode(audioData);
    audioData.close();
  }

  await encoder.flush();
  encoder.close();

  if (encoderError) {
    throw encoderError instanceof Error
      ? encoderError
      : new Error('AAC encoding failed');
  }

  muxerRef.finalize();
  const { buffer } = muxer.target;
  return new Blob([buffer], { type: 'audio/mp4' });
}

// ─── Decode ───────────────────────────────────────────────────────────────────

async function decodeToAudioBuffer(blob: Blob): Promise<AudioBuffer> {
  const scope = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextRef = scope.AudioContext ?? scope.webkitAudioContext;
  if (!AudioContextRef) {
    throw new Error('Web Audio API is unavailable');
  }

  const context = new AudioContextRef();
  try {
    const arrayBuffer = await blob.arrayBuffer();
    return await context.decodeAudioData(arrayBuffer);
  } finally {
    void context.close();
  }
}

// ─── Public entry point ───────────────────────────────────────────────────────

const relabel = (blob: Blob, type: string): Blob =>
  blob.type === type ? blob : new Blob([blob], { type });

/**
 * Converts a recorded audio blob into an iOS-playable upload.
 *
 * - Already an MP4/M4A or a real WAV (by magic bytes) → passed through with the
 *   correct MIME label, no re-encode.
 * - Anything else (WebM/Opus, or an empty/wrong type) → decoded and re-encoded
 *   to AAC/MP4, or to real WAV where the AAC encoder is unavailable.
 *
 * Never returns WebM: a recording that cannot be decoded at all rejects, so the
 * caller surfaces an error instead of uploading bytes iOS cannot play.
 */
export async function transcodeToUploadableAudio(
  blob: Blob,
): Promise<TranscodeResult> {
  const header = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  const sniffed = sniffAudioContainer(header);

  if (sniffed === 'mp4') {
    return { blob: relabel(blob, 'audio/mp4'), container: 'mp4' };
  }
  if (sniffed === 'wav') {
    return { blob: relabel(blob, 'audio/wav'), container: 'wav' };
  }

  const audioBuffer = await decodeToAudioBuffer(blob);

  if (await isAacEncodingSupported(audioBuffer.sampleRate, audioBuffer.numberOfChannels)) {
    try {
      const mp4 = await encodeAacMp4(audioBuffer);
      const mp4Header = new Uint8Array(await mp4.slice(0, 16).arrayBuffer());
      if (sniffAudioContainer(mp4Header) === 'mp4') {
        return { blob: mp4, container: 'mp4' };
      }
      console.warn(
        'audioTranscode: AAC output failed its own sniff; using WAV fallback',
      );
    } catch (error) {
      console.warn('audioTranscode: AAC encode failed; using WAV fallback', error);
    }
  }

  return { blob: audioBufferToWavBlob(audioBuffer), container: 'wav' };
}
